import { supabase } from './supabase'
import { nextOccurrence } from './taskRepeat'

/**
 * Letters: the things that want your attention rather than your money.
 *
 * docs/spec.md §15. Three segments, and the segment is derived from the row
 * rather than stored as a status — the same reasoning as payment state in
 * rule 4. "Waiting" is not a state someone sets and forgets; it is simply
 * what it means to have written down who you are waiting on.
 */

export type Segment = 'needs_you' | 'waiting' | 'all'

export type TaskKind = 'letter' | 'todo'

/** The four questions, as pipeline/letterSummary.ts answers them. */
export interface LetterSummary {
  what: string
  asks: string | null
  deadline: string | null
  deadlineSource: 'explicit_date' | 'within_days' | 'none'
  ifIgnored: string | null
  deadlineUnclear: boolean
}

export interface LetterTask {
  id: string
  kind: TaskKind
  /** RRULE, or null. Set means: ticking this off makes the next one. */
  repeatRule: string | null
  /** Days before the due date to warn, e.g. [7, 1]. The day itself always comes. */
  remindDaysBefore: number[]
  assignedTo: string | null
  assignmentNote: string | null
  summary: LetterSummary | null
  documentId: string | null
  title: string
  detail: string | null
  dueDate: string | null
  waitingOn: string | null
  doneAt: string | null
  spaceId: string
  createdAt: string
}

export function segmentOf(task: LetterTask): Exclude<Segment, 'all'> | 'done' {
  if (task.doneAt) return 'done'
  return task.waitingOn ? 'waiting' : 'needs_you'
}

/**
 * Sort by what is most pressing. A deadline that has passed comes first, then
 * the nearest deadline, then everything without one — undated work is real but
 * it never outranks a date.
 */
export function byUrgency(a: LetterTask, b: LetterTask): number {
  if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate)
  if (a.dueDate) return -1
  if (b.dueDate) return 1
  return b.createdAt.localeCompare(a.createdAt)
}

/**
 * Tasks, of one kind or of both.
 *
 * The default is 'letter', deliberately. Letters existed first and every
 * existing caller means letters — a to-do list leaking into the Brieven tab
 * is exactly what happened when this took no argument at all, because adding
 * a second kind to a shared table silently widens every query that was written
 * before the second kind existed.
 *
 * Filtering in the database rather than after: a member may only read the
 * tasks assigned to them, and doing the narrowing here keeps that one rule in
 * one place.
 */
export async function loadLetters(
  kind: TaskKind | 'all' = 'letter',
): Promise<LetterTask[]> {
  const query = supabase
    .from('tasks')
    .select('id, kind, assigned_to, assignment_note, summary, document_id, title, detail, due_date, waiting_on, done_at, space_id, created_at, repeat_rule, remind_days_before')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  const { data, error } = kind === 'all' ? await query : await query.eq('kind', kind)
  if (error) throw error

  return (data ?? []).map((t) => ({
    id: t.id as string,
    kind: (t.kind as TaskKind) ?? 'letter',
    repeatRule: (t.repeat_rule as string | null) ?? null,
    remindDaysBefore: (t.remind_days_before as number[] | null) ?? [],
    assignedTo: (t.assigned_to as string | null) ?? null,
    assignmentNote: (t.assignment_note as string | null) ?? null,
    summary: (t.summary as LetterSummary | null) ?? null,
    documentId: (t.document_id as string) ?? null,
    title: t.title as string,
    detail: (t.detail as string) ?? null,
    dueDate: (t.due_date as string) ?? null,
    waitingOn: (t.waiting_on as string) ?? null,
    doneAt: (t.done_at as string) ?? null,
    spaceId: t.space_id as string,
    createdAt: t.created_at as string,
  }))
}

/**
 * Tick something off.
 *
 * Through `set_task_done` rather than a direct update, because rule 9 wants
 * the activity_log row written in the same transaction as the change, and a
 * browser cannot promise that across two statements. The function is also
 * idempotent, so a double tap on a slow connection does not rewrite when the
 * thing was actually finished.
 *
 * `userId` is no longer used — the database takes `auth.uid()`, which is the
 * only version of that answer a client cannot lie about. It stays in the
 * signature so every caller did not have to change on the same day.
 */
export async function markHandled(
  taskId: string,
  _userId?: string,
  /**
   * A repeating task hands its own next date in, worked out in the browser with
   * the rrule library (rule 19 — Postgres has no notion of RRULE). It is a
   * convenience, not money: get it wrong and a chore sits on the wrong day,
   * which is one tap to move.
   */
  nextDue?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_task_done', {
    p_task_id: taskId,
    p_done: true,
    p_next_due: nextDue ?? null,
  })
  if (error) throw error
  // Whoever asked is told by the trigger in 0018, in this same transaction —
  // not by this client, which may be closed a second later.
}

export async function setWaitingOn(taskId: string, waitingOn: string | null): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({ waiting_on: waitingOn?.trim() || null })
    .eq('id', taskId)
  if (error) throw error
}

export async function setDueDate(taskId: string, dueDate: string | null): Promise<void> {
  const { error } = await supabase.from('tasks').update({ due_date: dueDate }).eq('id', taskId)
  if (error) throw error
}

/** Dutch, and relative where that is what a person actually thinks in. */
export function describeDue(dueDate: string | null, today = new Date()): string {
  if (!dueDate) return 'geen datum'
  const due = new Date(dueDate + 'T00:00:00')
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const days = Math.round((due.getTime() - start.getTime()) / 86400000)

  if (days < -1) return `${Math.abs(days)} dagen te laat`
  if (days === -1) return 'gisteren'
  if (days === 0) return 'vandaag'
  if (days === 1) return 'morgen'
  if (days <= 14) return `over ${days} dagen`
  return due.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })
}

/**
 * Things that need doing that are not letters.
 *
 * Same table, same closure loop, different list. A household to-do is a letter
 * without a letter — spec.md's tasks model already had everything it needed
 * except a label and a name to hand it to, which is what 0018 adds.
 */
export interface NewTodo {
  spaceId: string
  title: string
  detail: string | null
  dueDate: string | null
  assignedTo: string | null
  /**
   * Required when assigning, and deliberately so. "Bel de verzekering, vraag
   * naar de nieuwe polis" is a task someone can act on; "verzekering" is a
   * note to self that means nothing in someone else's hands.
   */
  assignmentNote: string | null
}

export async function createTodo(todo: NewTodo): Promise<string> {
  if (todo.assignedTo && !todo.assignmentNote?.trim()) {
    throw new Error('Zeg erbij wat er precies moet gebeuren.')
  }

  // One statement. The task row and its activity_log entry are written by
  // `create_todo` inside one transaction (rule 9) — the client used to do them
  // as two, which meant a closed tab between them left a task with no record
  // of where it came from.
  const { data, error } = await supabase.rpc('create_todo', {
    p_space_id: todo.spaceId,
    p_title: todo.title,
    p_due_date: todo.dueDate || null,
    p_assigned_to: todo.assignedTo,
    p_assignment_note: todo.assignmentNote,
    p_detail: todo.detail,
  })
  if (error) throw new Error(dutchError(error.message))

  return data as string
}

/** Correct a typo, or move a date. Admin only, enforced in 0030. */
export async function updateTodo(
  taskId: string,
  title: string,
  dueDate: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('update_todo', {
    p_task_id: taskId,
    p_title: title,
    p_due_date: dueDate,
  })
  if (error) throw new Error(dutchError(error.message))
}

/**
 * Postgres speaks Postgres. The two rules that a person can actually trip over
 * are raised with a Dutch message on purpose, so they arrive readable; anything
 * else is ours to fix, not theirs to decode.
 */
function dutchError(message: string): string {
  if (/hoogstens drie herinneringen/i.test(message)) {
    return 'Hoogstens drie herinneringen per taak.'
  }
  if (/zeg erbij|waar gaat het over|hoort niet bij deze ruimte/i.test(message)) {
    return message.charAt(0).toUpperCase() + message.slice(1) + '.'
  }
  return 'Opslaan lukte niet.'
}

/** Hand an existing task to someone, or take it back. */
export async function assignTask(
  taskId: string,
  userId: string | null,
  note: string,
): Promise<void> {
  if (userId && !note.trim()) throw new Error('Zeg erbij wat er precies moet gebeuren.')

  const { error } = await supabase.rpc('assign_task', {
    p_task_id: taskId,
    p_user_id: userId,
    p_note: userId ? note : null,
  })
  if (error) throw new Error(dutchError(error.message))
}

/**
 * When to be warned, in days before the due date.
 *
 * At most three, and the day itself is not one of them — that one always comes.
 * Three is a product limit rather than a technical one: a task that asks for
 * attention four times does not need another reminder, it needs another date.
 */
export async function setTaskReminders(taskId: string, days: number[]): Promise<void> {
  const { error } = await supabase.rpc('set_task_reminders', {
    p_task_id: taskId,
    p_days: days,
  })
  if (error) throw new Error(dutchError(error.message))
}

/** Set or clear how often a task comes back. Admin only, enforced in 0036. */
export async function setTaskRepeat(taskId: string, rule: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_task_repeat', {
    p_task_id: taskId,
    p_rule: rule,
  })
  if (error) throw new Error(dutchError(error.message))
}

/**
 * Tick a task off, and let a repeating one make its successor.
 *
 * One place, so that "done" means the same thing from the list, the calendar
 * and the parents' screen — the next chore appearing must not depend on which
 * screen you happened to be looking at.
 */
export async function completeTask(task: LetterTask, userId: string): Promise<void> {
  const next = task.repeatRule ? nextOccurrence(task.repeatRule, task.dueDate) : null
  await markHandled(task.id, userId, next)
}

/** Undo an accidental tick. The loop is only trustworthy if it runs both ways. */
export async function markNotDone(taskId: string): Promise<void> {
  const { error } = await supabase.rpc('set_task_done', {
    p_task_id: taskId,
    p_done: false,
    p_next_due: null,
  })
  if (error) throw error
}
